// SPDX-License-Identifier: GPL-3.0-or-later

import Peer, { type DataConnection, type MediaConnection } from 'peerjs';
import type { IceServer, PeerId, RoomName } from '@ffb/shared';

export interface PeerListEntry {
  peerId: PeerId;
}

export type SignalingMetadata = Record<string, unknown> | null;

export interface IncomingCallEvent {
  from: PeerId;
  metadata: SignalingMetadata;
  answer(localStream: MediaStream): void;
  onStream(cb: (stream: MediaStream) => void): void;
  onClose(cb: () => void): void;
}

export interface OutgoingCallHandle {
  peerId: PeerId;
  onStream(cb: (stream: MediaStream) => void): void;
  onClose(cb: () => void): void;
  close(): void;
}

export interface CallOptions {
  metadata?: Record<string, unknown>;
}

export interface IncomingDataEvent {
  from: PeerId;
  metadata: SignalingMetadata;
}

export interface DataHandle {
  peerId: PeerId;
  close(): void;
}

export interface SignalingService {
  open(room: RoomName): Promise<PeerId>;
  listPeers(): Promise<PeerId[]>;
  call(peerId: PeerId, localStream: MediaStream, options?: CallOptions): OutgoingCallHandle;
  openData(peerId: PeerId, metadata?: Record<string, unknown>): DataHandle;
  onIncomingCall(cb: (call: IncomingCallEvent) => void): void;
  onIncomingData(cb: (ev: IncomingDataEvent) => void): void;
  onError(cb: (err: Error) => void): void;
  close(): void;
}

export interface PeerJsSignalingOptions {
  host: string;
  port: number;
  secure: boolean;
  path: string;
  iceServers: IceServer[];
  peerIdPrefix?: string;
}

// Тонкая обёртка над peerjs: прячем PeerJS API и даём ViewModel семантику звонков.
// Наш PeerServer запускается с allow_discovery=true → listAllPeers возвращает зарегистрированных.
export class PeerJsSignalingService implements SignalingService {
  private peer: Peer | null = null;
  private readonly opts: PeerJsSignalingOptions;
  private room: RoomName | null = null;
  private incomingCb: ((call: IncomingCallEvent) => void) | null = null;
  private incomingDataCb: ((ev: IncomingDataEvent) => void) | null = null;
  private errorCb: ((err: Error) => void) | null = null;
  private dataConnections = new Map<string, DataConnection>();

  constructor(opts: PeerJsSignalingOptions) {
    this.opts = opts;
  }

  open(room: RoomName): Promise<PeerId> {
    this.room = room;
    return new Promise((resolve, reject) => {
      const prefix = this.opts.peerIdPrefix ?? 'ffb';
      const suffix = Math.random().toString(36).slice(2, 10);
      // peerId = "<prefix>-<room>-<suffix>" — это позволяет listPeers() отобрать участников комнаты.
      const id = `${prefix}-${room}-${suffix}`;
      const peer = new Peer(id, {
        host: this.opts.host,
        port: this.opts.port,
        secure: this.opts.secure,
        path: this.opts.path,
        config: { iceServers: this.opts.iceServers },
        debug: 1,
      });
      this.peer = peer;

      peer.on('open', (openedId) => resolve(openedId));
      peer.on('error', (err) => {
        // ID-conflict и другие ошибки подключения — отдаём наружу.
        this.errorCb?.(err instanceof Error ? err : new Error(String(err)));
        if (peer.destroyed) reject(err);
      });
      peer.on('call', (mc) => this.handleIncoming(mc));
      peer.on('connection', (dc) => this.handleIncomingData(dc));
    });
  }

  async listPeers(): Promise<PeerId[]> {
    const peer = this.requirePeer();
    if (!this.room) throw new Error('SignalingService: room is not set');
    const room = this.room;
    return new Promise((resolve, reject) => {
      peer.listAllPeers((list) => {
        try {
          const prefix = `${this.opts.peerIdPrefix ?? 'ffb'}-${room}`;
          resolve(list.filter((id) => id.startsWith(prefix) && id !== peer.id));
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      });
    });
  }

  call(peerId: PeerId, localStream: MediaStream, options?: CallOptions): OutgoingCallHandle {
    // metadata пробрасывается в PeerJS, приходит другой стороне как mc.metadata.
    const mc = this.requirePeer().call(peerId, localStream, {
      metadata: options?.metadata,
    });
    const onStreamCbs: Array<(s: MediaStream) => void> = [];
    const onCloseCbs: Array<() => void> = [];
    if (mc) {
      mc.on('stream', (s) => onStreamCbs.forEach((cb) => cb(s)));
      mc.on('close', () => onCloseCbs.forEach((cb) => cb()));
      mc.on('error', (e) => this.errorCb?.(e instanceof Error ? e : new Error(String(e))));
    }
    return {
      peerId,
      onStream: (cb) => onStreamCbs.push(cb),
      onClose: (cb) => onCloseCbs.push(cb),
      close: () => mc?.close(),
    };
  }

  openData(peerId: PeerId, metadata?: Record<string, unknown>): DataHandle {
    // DataConnection используется только для передачи metadata (имени) в обратную сторону.
    // Собственные сообщения не шлём — достаточно, что peerjs доставит metadata в 'connection'-событие.
    const dc = this.requirePeer().connect(peerId, { metadata, reliable: true });
    this.dataConnections.set(peerId, dc);
    dc.on('close', () => this.dataConnections.delete(peerId));
    dc.on('error', (e) => this.errorCb?.(e instanceof Error ? e : new Error(String(e))));
    return {
      peerId,
      close: () => {
        try {
          dc.close();
        } catch {
          /* уже закрыт */
        }
        this.dataConnections.delete(peerId);
      },
    };
  }

  onIncomingCall(cb: (call: IncomingCallEvent) => void): void {
    this.incomingCb = cb;
  }

  onIncomingData(cb: (ev: IncomingDataEvent) => void): void {
    this.incomingDataCb = cb;
  }

  onError(cb: (err: Error) => void): void {
    this.errorCb = cb;
  }

  close(): void {
    for (const dc of this.dataConnections.values()) {
      try {
        dc.close();
      } catch {
        /* ignore */
      }
    }
    this.dataConnections.clear();
    this.peer?.destroy();
    this.peer = null;
  }

  private handleIncoming(mc: MediaConnection): void {
    if (!this.incomingCb) return;
    const onStreamCbs: Array<(s: MediaStream) => void> = [];
    const onCloseCbs: Array<() => void> = [];
    this.incomingCb({
      from: mc.peer,
      metadata: (mc.metadata as SignalingMetadata) ?? null,
      answer: (localStream) => {
        mc.answer(localStream);
        mc.on('stream', (s) => onStreamCbs.forEach((cb) => cb(s)));
        mc.on('close', () => onCloseCbs.forEach((cb) => cb()));
      },
      onStream: (cb) => onStreamCbs.push(cb),
      onClose: (cb) => onCloseCbs.push(cb),
    });
  }

  private handleIncomingData(dc: DataConnection): void {
    // Держим reference, чтобы peerjs не потерял канал; закрываем по close() или из close().
    this.dataConnections.set(dc.peer, dc);
    dc.on('close', () => this.dataConnections.delete(dc.peer));
    dc.on('error', (e) => this.errorCb?.(e instanceof Error ? e : new Error(String(e))));
    this.incomingDataCb?.({
      from: dc.peer,
      metadata: (dc.metadata as SignalingMetadata) ?? null,
    });
  }

  private requirePeer(): Peer {
    if (!this.peer) throw new Error('SignalingService not opened');
    return this.peer;
  }
}

// Сервер ожидает peerId вида "<prefix>-<room>-<suffix>" → listPeers отфильтрует только нашу комнату.
export function roomPeerIdPrefix(prefix: string, room: RoomName): string {
  return `${prefix}-${room}`;
}
